import {
  Box3,
  BufferAttribute,
  BufferGeometry,
  Frustum,
  Group,
  LinearFilter,
  LinearMipmapLinearFilter,
  Material,
  Matrix4,
  Mesh,
  MeshBasicMaterial,
  MeshLambertMaterial,
  Object3D,
  PerspectiveCamera,
  SRGBColorSpace,
  Sphere,
  Texture,
  Vector3,
} from "three";
import { DRACOLoader } from "three/addons/loaders/DRACOLoader.js";
import { GLTFLoader } from "three/addons/loaders/GLTFLoader.js";
import type { Bbox3 } from "../geo/bbox";
import type { SceneOrigin } from "../geo/origin";
import type { MeshBundle, MeshTileEntry } from "../io/meshBundle";
import { fetchTileGlb, type MeshLodLevel, type MeshServerOptions } from "../io/meshServer";
import { MeshWorkerPool, type ParsedTile } from "../io/meshWorkerPool";
import { parseMtl, type MtlMaterial } from "../io/mtl";
import { estimateObjBounds, exactObjBounds } from "../io/objBounds";
import type { MeshOffset } from "../io/offset";
import type { CacheId } from "../io/rangeCache";
import { rangeCache } from "../io/rangeCache";
import { isAbort, Semaphore } from "../util/async";

export type MeshShading = "unlit" | "lit";

/**
 * `MeshOffset.source` placeholder used when no sidecar was found and none was supplied.
 * Distinct from a real source string so {@link MeshLayer.offsetKnown} can tell the two apart.
 */
const UNKNOWN_OFFSET_SOURCE = "none — set manually to place this mesh";

/** How tile extents are determined before the tiles themselves are read. */
export type TileLocateMode = "exact" | "fast";

export interface MeshLayerOptions {
  /**
   * Combined geometry + texture budget before the least useful full-resolution tiles are
   * downgraded back to their cheap preview (see the class doc comment). Every located tile's
   * preview counts against this too, but previews are small enough by design that the budget
   * is really governing how many tiles can be at full resolution at once.
   */
  budgetBytes?: number;
  maxTextureSize?: number;
  maxConcurrentLoads?: number;
  shading?: MeshShading;
  /**
   * Identity used to persist tile extents across sessions (see `DatasetSource.cache`).
   * Without it, `locateTiles` re-scans the archive every time the mesh is opened.
   */
  cache?: CacheId;
  /**
   * When set, each tile is first requested pre-converted to Draco-compressed binary glTF
   * from the server (`POST /api/mesh/tile` — see `io/meshServer.ts`), which is far faster to
   * parse than hand-decoding the tile's raw OBJ text (this project's "fastest way to display
   * a textured mesh" pick). Absent for local file drops and loose (non-zip) Connect files,
   * which have no single fetchable archive for the server to convert from — those always use
   * the direct OBJ path below. A server tile that fails for any reason (network, cold start,
   * conversion error) falls back to that same direct OBJ path per-tile, so the mesh always
   * ends up loaded even if a server hiccup made it slower.
   */
  server?: MeshServerOptions;
  /** Called whenever visible content changed and a repaint is needed. */
  onChange?: () => void;
}

export interface MeshLayerStats {
  tiles: number;
  loaded: number;
  loading: number;
  failed: number;
  triangles: number;
  bytes: number;
  located: number;
}

type TileState = "idle" | "loading" | "loaded" | "failed" | "empty";

/**
 * Where a loaded tile's content came from — `"server"` tiles have a preview/full split to
 * swap between (see the class doc comment); `"bundle"` tiles (parsed client-side, either
 * because no `server` option was configured or as that path's per-tile fallback) have no
 * cheap tier available, so their content always lives in the `full*` fields and, once
 * released, has to be re-fetched and re-parsed from scratch rather than downgraded.
 */
type TileSource = "server" | "bundle";

interface MeshTile {
  entry: MeshTileEntry;
  state: TileState;
  /** Extent in scene coordinates; estimated before loading, exact afterwards. */
  box: Box3 | null;
  approximate: boolean;
  /** Set once the tile's first successful load tells us which path it came from. */
  source: TileSource | null;
  /**
   * The cheap tier's content, once loaded — a `server` tile only. Kept resident for as long
   * as the tile has anything loaded at all, so `full*` can always be safely released back
   * down to this without a network round trip. `Object3D` because a server-converted glTF's
   * scene graph may hold several meshes, one per material.
   */
  previewObject: Object3D | null;
  previewTextures: Texture[];
  previewBytes: number;
  previewTriangles: number;
  /**
   * The full-resolution tier's content, once loaded. Populated for `server` tiles only once
   * the tile is large enough on screen to be worth upgrading (see `UPGRADE_SCREEN_SIZE`); for
   * `bundle` tiles, this is the tile's only content, loaded directly.
   */
  fullObject: Object3D | null;
  fullTextures: Texture[];
  fullBytes: number;
  fullTriangles: number;
  /** Whether an upgrade fetch for this tile is currently in flight. */
  fullState: "idle" | "loading";
  /** Which of `previewObject`/`fullObject` is the one actually added to `this.group`. */
  active: "preview" | "full" | null;
  lastVisible: number;
  screenSize: number;
  /** Aborts the tile's initial (preview, or bundle-path direct) load. */
  abort: AbortController | null;
  /** Aborts an in-flight upgrade fetch, independent of `abort` above. */
  fullAbort: AbortController | null;
  error?: string;
}

const DEFAULT_BUDGET = 800 * 1024 * 1024;
/**
 * On-screen diameter (in pixels) a `server` tile's bounding sphere must reach before its
 * full-resolution asset is fetched to replace the preview already showing. Below this, the
 * preview's much lower triangle count and 256px textures are visually indistinguishable from
 * the full tile anyway, so there is nothing to gain from spending the extra bytes.
 */
const UPGRADE_SCREEN_SIZE = 480;
/**
 * Below this, a tile that previously earned an upgrade drops back to its preview. Kept well
 * below `UPGRADE_SCREEN_SIZE` (rather than the same value) so a tile sitting near the
 * threshold — e.g. the camera drifting slightly — doesn't repeatedly fetch and release the
 * same full-resolution asset.
 */
const DOWNGRADE_SCREEN_SIZE = 280;
/**
 * How many tiles `locateTiles` scans at once. Each scan is metadata-only (a binary search
 * plus a handful of small samples, not the tile itself), so this can safely run well above
 * `maxConcurrentLoads` — it's latency-bound, not bandwidth-bound, and `HttpRangeSource`'s own
 * gate still caps the actual number of requests in flight.
 */
const LOCATE_CONCURRENCY = 8;

/**
 * One `DRACOLoader`/`GLTFLoader` pair shared by every `MeshLayer` instance — three.js's own
 * guidance is to reuse a single `DRACOLoader`, since each one spins up its own decoder
 * worker pool. The decoder files are vendored into `public/draco/` at build time (copied
 * from `three/examples/jsm/libs/draco/gltf/`, the variant meant for glTF's
 * `KHR_draco_mesh_compression`) rather than fetched from a CDN, matching the rest of this
 * project's "no external hosts" deployment shape.
 */
let sharedGltfLoader: GLTFLoader | null = null;
function gltfLoader(): GLTFLoader {
  if (!sharedGltfLoader) {
    const draco = new DRACOLoader();
    draco.setDecoderPath("./draco/");
    sharedGltfLoader = new GLTFLoader();
    sharedGltfLoader.setDRACOLoader(draco);
  }
  return sharedGltfLoader;
}

/**
 * Streams a tiled, textured photogrammetry mesh.
 *
 * Tiles are pulled from the bundle on demand, ordered by how much of the screen they cover —
 * the reference mesh is 1.8 GB across 60 tiles, far more than a tab can hold at once, so what
 * matters is that the tiles near the camera are the ones resident at full detail.
 *
 * Every located tile is first loaded at a cheap "preview" tier (server-converted tiles only —
 * heavily simplified geometry, small textures; see `MeshLodLevel` in
 * `server/src/mesh/convertStaged.ts`), small enough that the *entire* mesh can be resident at
 * once well inside a ~1 GB budget. A tile is only fetched at full resolution once it is large
 * enough on screen to be worth it (`UPGRADE_SCREEN_SIZE`), and drops back to its preview —
 * never disappearing outright — once it shrinks back down or the budget is under pressure
 * (`enforceBudget`). Tiles loaded via the client-side fallback path (no `server` configured,
 * or a per-tile server hiccup) have no such cheap tier and are loaded directly at full
 * resolution instead, same as before this scheme existed.
 */
export class MeshLayer {
  readonly group = new Group();
  readonly tiles: MeshTile[] = [];
  offset: MeshOffset | null = null;
  /** Mesh offset minus scene origin, applied to vertices during parsing. */
  private shift: [number, number, number] = [0, 0, 0];

  /** Created on first parse: a layer that only ever locates tiles never spawns workers. */
  private pool: MeshWorkerPool | null = null;
  private readonly budget: number;
  private readonly maxTextureSize: number;
  private readonly maxConcurrent: number;
  private readonly maxConcurrentUpgrades = 3;
  private readonly cache?: CacheId;
  private readonly server?: MeshServerOptions;
  private shading: MeshShading;
  private inFlight = 0;
  private inFlightUpgrades = 0;
  private frame = 0;
  private lastUpdate = 0;
  private disposed = false;
  private bytesUsed = 0;

  private readonly frustum = new Frustum();
  private readonly projScreen = new Matrix4();
  private readonly sphere = new Sphere();

  constructor(
    readonly origin: SceneOrigin,
    readonly bundle: MeshBundle,
    private readonly options: MeshLayerOptions = {},
  ) {
    this.budget = options.budgetBytes ?? DEFAULT_BUDGET;
    this.maxTextureSize = options.maxTextureSize ?? 2048;
    this.maxConcurrent = options.maxConcurrentLoads ?? 6;
    this.cache = options.cache;
    this.server = options.server;
    // Photogrammetry textures already contain baked lighting, so relighting them tends to
    // look wrong; unlit is the faithful default. Server-converted tiles are always unlit
    // (baked in at conversion time, for the same reason) — the lit/unlit toggle only ever
    // rebuilds tiles parsed the direct way; see `setShading`.
    this.shading = options.shading ?? "unlit";
    this.group.name = "mesh";
  }

  /**
   * Establishes the georeferencing and enumerates the tiles. Does no bulk I/O, so the
   * layer is ready to start streaming immediately; extents are filled in afterwards by
   * {@link locateTiles}. `overrideOffset` supplies the shift when no sidecar was found.
   *
   * A missing offset used to be a hard failure — the mesh never got as far as `this.mesh`
   * being set, so it neither rendered nor could its panel's "Set offset manually…" ever be
   * reached to fix it. Falling back to (0,0,0) instead means the tiles always render, even
   * if placed arbitrarily until a real or manual offset is supplied.
   */
  async prepare(overrideOffset?: MeshOffset | null): Promise<void> {
    this.offset = overrideOffset ?? this.bundle.offset ?? {
      east: 0,
      north: 0,
      height: 0,
      source: UNKNOWN_OFFSET_SOURCE,
    };

    this.origin.setOnce(this.offset.east, this.offset.north, this.offset.height);
    this.shift = [
      this.offset.east - this.origin.east,
      this.offset.north - this.origin.north,
      this.offset.height - this.origin.height,
    ];

    for (const entry of this.bundle.tiles) {
      this.tiles.push({
        entry,
        state: "idle",
        box: null,
        approximate: true,
        source: null,
        previewObject: null,
        previewTextures: [],
        previewBytes: 0,
        previewTriangles: 0,
        fullObject: null,
        fullTextures: [],
        fullBytes: 0,
        fullTriangles: 0,
        fullState: "idle",
        active: null,
        lastVisible: 0,
        screenSize: 0,
        abort: null,
        fullAbort: null,
      });
    }
  }

  /**
   * Works out where each tile sits so loads can be ordered nearest-first.
   *
   * `"exact"` scans every tile's vertex block — about 12 % of the OBJ bytes, in one
   * sequential read per tile — and yields the true extent. `"fast"` instead takes a few
   * small samples per tile: roughly a tenth of the bytes, but many more requests, and it
   * can under-cover a tile by around a quarter of its width. Either way the pass runs in
   * the background and tiles still load while it is in progress. Runs the same way whether
   * or not tile content itself comes from the server (see `options.server`) — it reads
   * straight from the archive (`this.bundle`), which is opened client-side regardless, to
   * discover the ZIP's tile list before anything is fetched.
   *
   * A `cache` identity on the layer lets a result be reused across sessions: once a tile's
   * exact extent has been scanned and persisted, later opens of the same file (any locate
   * mode) skip the scan for that tile entirely instead of re-reading 12 % of the archive.
   */
  async locateTiles(
    mode: TileLocateMode,
    onProgress?: (done: number, total: number) => void,
    signal?: AbortSignal,
  ): Promise<void> {
    let done = 0;
    // Tiles are located concurrently rather than one at a time: each is metadata-only I/O
    // (see `LOCATE_CONCURRENCY`), and finding one tile tells you nothing about where the
    // next one is, so there's no reason to wait for it to finish first.
    const gate = new Semaphore(LOCATE_CONCURRENCY);
    await Promise.all(
      this.tiles.map((tile) =>
        gate.run(async () => {
          if (this.disposed || signal?.aborted) return;
          // A tile already parsed in full has a measured box; nothing to improve.
          const needsExact = mode === "exact" && tile.approximate;
          if ((!tile.box || needsExact) && tile.state !== "loaded") {
            try {
              const cached = this.cache ? await rangeCache.getBounds(this.cache.key, tile.entry.objPath) : undefined;
              // A cached exact extent satisfies either mode; a cached estimate only satisfies
              // a fast request — an exact request still needs to scan for the real thing.
              if (cached && (!cached.approximate || mode !== "exact")) {
                tile.box = this.boxFromLocal(cached.bbox);
                tile.approximate = cached.approximate;
              } else {
                const local =
                  mode === "exact"
                    ? await exactObjBounds(this.bundle, tile.entry, signal)
                    : await estimateObjBounds(this.bundle, tile.entry, signal);
                if (local) {
                  tile.box = this.boxFromLocal(local);
                  tile.approximate = mode !== "exact";
                  if (this.cache) {
                    rangeCache.putBounds(this.cache.key, this.cache.group, tile.entry.objPath, local, tile.approximate);
                  }
                }
              }
            } catch (error) {
              if (!isAbort(error)) console.warn(`[mesh] could not locate ${tile.entry.name}:`, error);
            }
          }
          onProgress?.(++done, this.tiles.length);
        }),
      ),
    );
  }

  /** Converts an OBJ-local East/North/Up box into a scene-space box. */
  private boxFromLocal(local: Bbox3): Box3 {
    const [dx, dy, dz] = this.shift;
    const minX = local.minX + dx;
    const maxX = local.maxX + dx;
    const minY = local.minY + dy;
    const maxY = local.maxY + dy;
    const minZ = local.minZ + dz;
    const maxZ = local.maxZ + dz;
    // East/North/Up -> three.js X/Y/Z, which flips and swaps the horizontal axes.
    return new Box3(new Vector3(minX, minZ, -maxY), new Vector3(maxX, maxZ, -minY));
  }

  /** False when the mesh is placed at a fallback offset rather than a real or manual one. */
  get offsetKnown(): boolean {
    return this.offset !== null && this.offset.source !== UNKNOWN_OFFSET_SOURCE;
  }

  /** Tiles whose extent is still the cheap estimate rather than a measured one. */
  get approximateTiles(): number {
    return this.tiles.filter((tile) => tile.approximate && tile.box).length;
  }

  /** Tiles whose position is not yet known, which therefore cannot be prioritised. */
  get unlocatedTiles(): number {
    return this.tiles.filter((tile) => !tile.box).length;
  }

  update(camera: PerspectiveCamera, viewportHeight: number, now: number): void {
    if (this.disposed || !this.group.visible) return;
    if (now - this.lastUpdate < 200) return;
    this.lastUpdate = now;
    this.frame++;

    // Streaming runs on its own cadence rather than inside the render loop, so the camera
    // matrices cannot be assumed fresh from a previous frame.
    camera.updateMatrixWorld();
    camera.matrixWorldInverse.copy(camera.matrixWorld).invert();
    this.group.updateMatrixWorld();
    this.projScreen.multiplyMatrices(camera.projectionMatrix, camera.matrixWorldInverse);
    this.frustum.setFromProjectionMatrix(this.projScreen);
    const pixelsPerRadian = viewportHeight / (2 * Math.tan(((camera.fov / 2) * Math.PI) / 180));

    const candidates: Array<{ tile: MeshTile; priority: number }> = [];
    const upgradeCandidates: MeshTile[] = [];

    for (const tile of this.tiles) {
      if (tile.state === "failed" || tile.state === "empty") continue;
      let priority: number;
      if (!tile.box) {
        // Position unknown: still worth loading, but only after everything visible.
        priority = 1;
      } else {
        this.sphere.copy(boundingSphereOf(tile.box)).applyMatrix4(this.group.matrixWorld);
        const visible = this.frustum.intersectsSphere(this.sphere);
        const distance = Math.max(0.5, this.sphere.center.distanceTo(camera.position));
        const screenSize = (this.sphere.radius * 2 * pixelsPerRadian) / distance;
        tile.screenSize = visible ? screenSize : 0;
        if (visible) tile.lastVisible = this.frame;
        // Off-screen tiles keep a small non-zero score so panning does not always start
        // from nothing, but they never outrank something the user can see.
        priority = visible ? screenSize : screenSize * 0.05;
      }
      if (tile.state === "idle") candidates.push({ tile, priority });
      if (
        tile.source === "server" &&
        tile.active === "preview" &&
        tile.fullState === "idle" &&
        tile.screenSize >= UPGRADE_SCREEN_SIZE
      ) {
        upgradeCandidates.push(tile);
      }
      // Shrunk back down (or panned off-screen): release the full-resolution asset right
      // away rather than waiting for budget pressure — there is no benefit to holding onto
      // detail for a tile the camera has moved on from.
      if (tile.fullObject && tile.screenSize < DOWNGRADE_SCREEN_SIZE) this.releaseFull(tile);
    }

    this.enforceBudget();

    candidates.sort((a, b) => b.priority - a.priority);
    for (const candidate of candidates) {
      if (this.inFlight >= this.maxConcurrent) break;
      if (this.bytesUsed >= this.budget) break;
      void this.loadTile(candidate.tile);
    }

    upgradeCandidates.sort((a, b) => b.screenSize - a.screenSize);
    for (const tile of upgradeCandidates) {
      if (this.inFlightUpgrades >= this.maxConcurrentUpgrades) break;
      if (this.bytesUsed >= this.budget) break;
      void this.upgradeTile(tile);
    }
  }

  private async loadTile(tile: MeshTile): Promise<void> {
    if (tile.state !== "idle" || this.disposed) return;
    tile.state = "loading";
    this.inFlight++;
    const controller = new AbortController();
    tile.abort = controller;

    try {
      if (this.server) {
        const loadedFromServer = await this.loadTileFromServer(tile, controller.signal, "preview");
        if (loadedFromServer) {
          tile.source = "server";
          return;
        }
        if (this.disposed || controller.signal.aborted) return;
        // Fall through to the direct OBJ path below — a server hiccup shouldn't leave the
        // tile stuck failed when the archive itself is perfectly readable client-side.
      }
      await this.loadTileFromBundle(tile, controller.signal);
      tile.source = "bundle";
    } catch (error) {
      if (isAbort(error)) {
        tile.state = "idle";
      } else {
        tile.state = "failed";
        tile.error = error instanceof Error ? error.message : String(error);
        console.warn(`[mesh] tile ${tile.entry.name} failed:`, error);
      }
    } finally {
      tile.abort = null;
      this.inFlight--;
    }
  }

  /**
   * Fetches the full-resolution asset for a tile currently showing its preview, and swaps it
   * in once ready. Mirrors `loadTile`'s bookkeeping but against `fullState`/`fullAbort`
   * instead of `state`/`abort`, and — unlike `loadTile` — a failure here simply leaves the
   * tile showing its preview rather than marking it failed: the preview is already a
   * perfectly good fallback, so there's nothing broken to report.
   */
  private async upgradeTile(tile: MeshTile): Promise<void> {
    if (tile.fullState !== "idle" || !this.server || this.disposed) return;
    tile.fullState = "loading";
    this.inFlightUpgrades++;
    const controller = new AbortController();
    tile.fullAbort = controller;

    try {
      await this.loadTileFromServer(tile, controller.signal, "full");
    } catch (error) {
      if (!isAbort(error)) console.warn(`[mesh] upgrade of ${tile.entry.name} failed:`, error);
    } finally {
      tile.fullAbort = null;
      tile.fullState = "idle";
      this.inFlightUpgrades--;
    }
  }

  /**
   * Loads a tile from the server's pre-converted, Draco-compressed glTF (see `options.server`
   * and `io/meshServer.ts`) — far less parsing work than the raw-OBJ path below. Returns
   * `false` (without throwing) on any failure so the caller can fall back (`loadTile` to the
   * direct OBJ path; `upgradeTile` to just keeping the preview) instead of the tile ending up
   * failed outright; throws only for things neither fallback can fix either (the layer being
   * disposed mid-request, an aborted load).
   */
  private async loadTileFromServer(tile: MeshTile, signal: AbortSignal, level: MeshLodLevel): Promise<boolean> {
    let glb: ArrayBuffer;
    try {
      glb = await fetchTileGlb(this.server!, tile.entry.objPath, level, signal);
    } catch (error) {
      if (isAbort(error)) throw error;
      console.warn(`[mesh] server conversion (${level}) of ${tile.entry.name} failed:`, error);
      return false;
    }
    if (this.disposed || signal.aborted) throw new DOMException("aborted", "AbortError");

    const parsed = await this.parseServerGlb(glb, tile.entry.name);
    if (!parsed) return false;
    if (this.disposed || signal.aborted) throw new DOMException("aborted", "AbortError");

    const { object, textures, triangles, bytes } = parsed;
    tile.box = new Box3().setFromObject(object);
    tile.approximate = false;
    if (level === "preview") {
      tile.previewObject = object;
      tile.previewTextures = textures;
      tile.previewTriangles = triangles;
      tile.previewBytes = bytes;
      tile.state = "loaded";
      tile.active = "preview";
    } else {
      if (tile.previewObject && tile.active === "preview") this.group.remove(tile.previewObject);
      tile.fullObject = object;
      tile.fullTextures = textures;
      tile.fullTriangles = triangles;
      tile.fullBytes = bytes;
      tile.active = "full";
    }
    this.bytesUsed += bytes;
    this.group.add(object);
    this.options.onChange?.();
    return true;
  }

  /** Parses a server-converted glTF and reduces it to what `loadTileFromServer` needs to
   *  store, whichever LOD level it is. Returns `null` (without throwing) on a parse failure. */
  private async parseServerGlb(
    glb: ArrayBuffer,
    tileName: string,
  ): Promise<{ object: Object3D; textures: Texture[]; triangles: number; bytes: number } | null> {
    const gltf = await new Promise<{ scene: Object3D }>((resolve, reject) => {
      gltfLoader().parse(glb, "", resolve, reject);
    }).catch((error: unknown) => {
      console.warn(`[mesh] server glTF for ${tileName} could not be parsed:`, error);
      return null;
    });
    if (!gltf) return null;

    const object = gltf.scene;
    // The glTF's vertex positions are in the OBJ's own local (unshifted) *and unrotated*
    // coordinates — obj2gltf copies OBJ x/y/z straight into the glTF buffers with no notion
    // of which axis is "up" — whereas the direct-parse path (`objParser.ts`) bakes East/
    // North/Up -> three.js X/Y/Z into every vertex as it parses (see its `yUp` remap: (x, z,
    // -y)). Reproducing that here as a plain transform keeps both loading paths in the same
    // frame: rotating -90° about X performs the identical (x, y, z) -> (x, z, -y) remap, and
    // the shift (in the OBJ's east/north/height units) is likewise rotated into scene
    // (X/Y/Z) units before being used as the position offset.
    object.rotation.set(-Math.PI / 2, 0, 0);
    object.position.set(this.shift[0], this.shift[2], -this.shift[1]);
    object.matrixAutoUpdate = false;
    object.updateMatrix();
    object.updateMatrixWorld(true);

    let triangles = 0;
    const textures: Texture[] = [];
    object.traverse((child) => {
      if (!(child instanceof Mesh)) return;
      const geometry = child.geometry as BufferGeometry;
      triangles += (geometry.index ? geometry.index.count : (geometry.attributes.position?.count ?? 0)) / 3;
      for (const material of materialsOf(child)) {
        const map = (material as MeshBasicMaterial).map;
        if (map) textures.push(map);
      }
    });

    return { object, textures, triangles, bytes: estimateObjectBytes(object) };
  }

  /** Parses a tile's raw OBJ/MTL/JPG directly, off the main thread (see `MeshWorkerPool`) —
   *  the path used when no `server` option is configured, and the per-tile fallback when the
   *  server conversion fails. */
  private async loadTileFromBundle(tile: MeshTile, signal: AbortSignal): Promise<void> {
    // The MTL is usually resolvable before the OBJ is even read — `entry.mtlPath` comes
    // from the zip's central directory, scanned up front, not from parsing this tile — so
    // it's fetched alongside the (much larger) OBJ instead of waited on afterwards.
    const earlyMtl = tile.entry.mtlPath ? this.readMtl(tile.entry.mtlPath, signal) : undefined;

    const bytes = await this.bundle.read(tile.entry.objPath, signal);
    if (this.disposed || signal.aborted) return;

    this.pool ??= new MeshWorkerPool();
    const parsed = await this.pool.parse(bytes, this.shift);
    if (this.disposed || signal.aborted) return;
    if (parsed.vertexCount === 0) {
      tile.state = "empty";
      return;
    }

    const materials = await this.loadMaterials(tile.entry, parsed, signal, earlyMtl);
    if (this.disposed || signal.aborted) {
      for (const texture of materials.textures) texture.dispose();
      return;
    }

    const mesh = this.buildMesh(parsed, materials.materials);
    tile.fullObject = mesh;
    tile.fullTextures = materials.textures;
    tile.fullTriangles = parsed.triangleCount;
    tile.fullBytes = estimateBytes(parsed, materials.textures);
    tile.box = new Box3().setFromBufferAttribute(mesh.geometry.getAttribute("position") as BufferAttribute);
    tile.approximate = false;
    tile.state = "loaded";
    tile.active = "full";
    this.bytesUsed += tile.fullBytes;
    this.group.add(mesh);
    this.options.onChange?.();
  }

  private buildMesh(parsed: ParsedTile, materials: Array<MeshBasicMaterial | MeshLambertMaterial>): Mesh {
    const geometry = new BufferGeometry();
    geometry.setAttribute("position", new BufferAttribute(parsed.positions, 3));
    if (parsed.uvs) geometry.setAttribute("uv", new BufferAttribute(parsed.uvs, 2));
    if (parsed.normals) {
      geometry.setAttribute("normal", new BufferAttribute(parsed.normals, 3));
    } else if (this.shading === "lit") {
      geometry.computeVertexNormals();
    }
    parsed.groups.forEach((group, index) => geometry.addGroup(group.start, group.count, index));
    geometry.computeBoundingSphere();
    geometry.computeBoundingBox();

    const mesh = new Mesh(geometry, materials.length === 1 ? materials[0] : materials);
    mesh.matrixAutoUpdate = false;
    mesh.updateMatrix();
    return mesh;
  }

  /** Loads the MTL for a tile and turns each referenced texture into a three material. */
  private async loadMaterials(
    entry: MeshTileEntry,
    parsed: ParsedTile,
    signal: AbortSignal,
    earlyMtl?: Promise<Map<string, MtlMaterial>>,
  ): Promise<{ materials: Array<MeshBasicMaterial | MeshLambertMaterial>; textures: Texture[] }> {
    let definitions = new Map<string, MtlMaterial>();
    const mtlPath =
      entry.mtlPath ??
      (parsed.materialLibs[0] ? this.bundle.resolve(entry.objPath, parsed.materialLibs[0]) : null);
    if (mtlPath && this.bundle.has(mtlPath)) {
      try {
        definitions = await (earlyMtl ?? this.readMtl(mtlPath, signal));
      } catch (error) {
        console.warn(`[mesh] could not read ${mtlPath}:`, error);
      }
    }

    const names = parsed.groups.length > 0 ? parsed.groups.map((g) => g.material) : [""];

    // Each material's texture is an independent fetch + decode; loading them together
    // instead of one at a time is a straight win once there's more than one per tile.
    const loaded = await Promise.all(
      names.map(async (name) => {
        const definition = definitions.get(name) ?? [...definitions.values()][0];
        let texture: Texture | null = null;
        if (definition?.diffuseMap && mtlPath) {
          const texturePath = this.bundle.resolve(mtlPath, definition.diffuseMap);
          if (texturePath && this.bundle.has(texturePath)) {
            texture = await this.loadTexture(texturePath, signal);
          }
        }
        return { material: this.createMaterial(definition, texture), texture };
      }),
    );

    const materials = loaded.map((l) => l.material);
    const textures = loaded.flatMap((l) => (l.texture ? [l.texture] : []));
    return { materials, textures };
  }

  /** Fetches and parses an MTL file. Split out so a fetch can be kicked off before the OBJ. */
  private async readMtl(mtlPath: string, signal: AbortSignal): Promise<Map<string, MtlMaterial>> {
    return parseMtl(await this.bundle.readText(mtlPath, signal));
  }

  private async loadTexture(path: string, signal: AbortSignal): Promise<Texture | null> {
    try {
      const blob = await this.bundle.blob(path, mimeTypeFor(path), signal);
      // OBJ texture coordinates have their origin at the bottom left, so the image is
      // flipped on decode and the texture is told not to flip it again.
      let bitmap = await createImageBitmap(blob, { imageOrientation: "flipY" });
      const largest = Math.max(bitmap.width, bitmap.height);
      if (largest > this.maxTextureSize) {
        const scale = this.maxTextureSize / largest;
        const resized = await createImageBitmap(bitmap, {
          resizeWidth: Math.max(1, Math.round(bitmap.width * scale)),
          resizeHeight: Math.max(1, Math.round(bitmap.height * scale)),
          resizeQuality: "high",
        });
        bitmap.close();
        bitmap = resized;
      }
      const texture = new Texture(bitmap as unknown as HTMLImageElement);
      texture.flipY = false;
      texture.colorSpace = SRGBColorSpace;
      texture.magFilter = LinearFilter;
      texture.minFilter = LinearMipmapLinearFilter;
      texture.generateMipmaps = true;
      texture.anisotropy = 8;
      texture.needsUpdate = true;
      return texture;
    } catch (error) {
      if (!isAbort(error)) console.warn(`[mesh] texture ${path} failed:`, error);
      return null;
    }
  }

  private createMaterial(
    definition: MtlMaterial | undefined,
    texture: Texture | null,
  ): MeshBasicMaterial | MeshLambertMaterial {
    const color = definition ? rgbToHex(definition.diffuse) : 0xffffff;
    const opacity = definition?.opacity ?? 1;
    const common = {
      color: texture ? 0xffffff : color,
      ...(texture ? { map: texture } : {}),
      transparent: opacity < 1,
      opacity,
    };
    return this.shading === "unlit" ? new MeshBasicMaterial(common) : new MeshLambertMaterial(common);
  }

  /**
   * Downgrades the least useful full-resolution tiles — least recently visible first, and
   * only ones already below the upgrade threshold, so this never fights the upgrade pass
   * above over a tile the camera is actually looking at — until the layer is back inside its
   * budget. A `server` tile drops back to its already-resident preview; a `bundle` tile (no
   * preview to fall back to) is unloaded outright, same as this layer's pre-LOD behaviour.
   */
  private enforceBudget(): void {
    if (this.bytesUsed <= this.budget) return;
    const resident = this.tiles
      .filter((tile) => tile.fullObject && tile.screenSize < UPGRADE_SCREEN_SIZE)
      .sort((a, b) => a.lastVisible - b.lastVisible);
    for (const tile of resident) {
      if (this.bytesUsed <= this.budget * 0.85) break;
      this.releaseFull(tile);
    }
  }

  /** Disposes a tile's full-resolution asset and, if it has a preview to fall back to, makes
   *  that the active object again; otherwise (a `bundle` tile) the tile goes back to `idle`
   *  and will be re-fetched from scratch if it becomes worth loading again. */
  private releaseFull(tile: MeshTile): void {
    if (!tile.fullObject) return;
    this.group.remove(tile.fullObject);
    disposeObject3D(tile.fullObject);
    for (const texture of tile.fullTextures) texture.dispose();
    this.bytesUsed -= tile.fullBytes;
    tile.fullObject = null;
    tile.fullTextures = [];
    tile.fullBytes = 0;
    tile.fullTriangles = 0;
    if (tile.previewObject) {
      tile.active = "preview";
      this.group.add(tile.previewObject);
    } else {
      tile.active = null;
      tile.state = "idle";
    }
    this.options.onChange?.();
  }

  /** Disposes everything resident for a tile — both LOD tiers — and puts it back to `idle`.
   *  Used for a full teardown (`dispose`) and for `setShading`, which needs every tile
   *  re-fetched from scratch since it changes material classes for the `bundle` path. */
  private resetTile(tile: MeshTile): void {
    if (tile.fullObject) {
      this.group.remove(tile.fullObject);
      disposeObject3D(tile.fullObject);
      for (const texture of tile.fullTextures) texture.dispose();
    }
    if (tile.previewObject) {
      this.group.remove(tile.previewObject);
      disposeObject3D(tile.previewObject);
      for (const texture of tile.previewTextures) texture.dispose();
    }
    this.bytesUsed -= tile.fullBytes + tile.previewBytes;
    tile.fullObject = tile.previewObject = null;
    tile.fullTextures = tile.previewTextures = [];
    tile.fullBytes = tile.previewBytes = 0;
    tile.fullTriangles = tile.previewTriangles = 0;
    tile.active = null;
    tile.fullState = "idle";
    tile.state = "idle";
    this.options.onChange?.();
  }

  setShading(shading: MeshShading): void {
    if (shading === this.shading) return;
    this.shading = shading;
    // Materials are rebuilt from scratch: swapping between unlit and lit changes the
    // material class, and lit shading additionally needs vertex normals. Server-converted
    // tiles are unaffected — their materials are baked in at conversion time (always unlit,
    // matching this layer's own default rationale) — but are reset and reloaded along with
    // everything else for simplicity; the re-fetch is a cheap cache hit either way.
    for (const tile of this.tiles) {
      if (tile.state === "loaded") this.resetTile(tile);
    }
  }

  setVisible(visible: boolean): void {
    this.group.visible = visible;
  }

  setOpacity(opacity: number): void {
    for (const tile of this.tiles) {
      for (const object of [tile.previewObject, tile.fullObject]) {
        if (!object) continue;
        object.traverse((child) => {
          if (!(child instanceof Mesh)) return;
          for (const material of materialsOf(child)) {
            material.opacity = opacity;
            material.transparent = opacity < 1;
            material.depthWrite = opacity >= 1;
            material.needsUpdate = true;
          }
        });
      }
    }
  }

  bounds(): Box3 {
    const box = new Box3();
    for (const tile of this.tiles) if (tile.box) box.union(tile.box);
    return box;
  }

  stats(): MeshLayerStats {
    let loaded = 0;
    let loading = 0;
    let failed = 0;
    let triangles = 0;
    let located = 0;
    for (const tile of this.tiles) {
      if (tile.state === "loaded") loaded++;
      if (tile.state === "loading" || tile.fullState === "loading") loading++;
      if (tile.state === "failed") failed++;
      if (tile.box) located++;
      triangles += tile.active === "full" ? tile.fullTriangles : tile.previewTriangles;
    }
    return { tiles: this.tiles.length, loaded, loading, failed, triangles, bytes: this.bytesUsed, located };
  }

  dispose(): void {
    this.disposed = true;
    for (const tile of this.tiles) {
      tile.abort?.abort();
      tile.fullAbort?.abort();
      this.resetTile(tile);
    }
    this.tiles.length = 0;
    this.pool?.dispose();
    this.pool = null;
  }
}

/** Disposes an object's geometry and materials (not its textures — callers dispose those
 *  separately since a tile tracks them apart from the object graph, e.g. to share one texture
 *  across several materials). Does not remove `object` from its parent. */
function disposeObject3D(object: Object3D): void {
  object.traverse((child) => {
    if (!(child instanceof Mesh)) return;
    (child.geometry as BufferGeometry).dispose();
    for (const material of materialsOf(child)) material.dispose();
  });
}

function boundingSphereOf(box: Box3): Sphere {
  const sphere = new Sphere();
  box.getBoundingSphere(sphere);
  return sphere;
}

/** Every material on a mesh, whether it's a single material or a per-group array. */
function materialsOf(mesh: Mesh): Material[] {
  return Array.isArray(mesh.material) ? mesh.material : [mesh.material];
}

function estimateBytes(parsed: ParsedTile, textures: Texture[]): number {
  let bytes = parsed.positions.byteLength;
  if (parsed.uvs) bytes += parsed.uvs.byteLength;
  if (parsed.normals) bytes += parsed.normals.byteLength;
  for (const texture of textures) bytes += estimateTextureBytes(texture);
  return bytes;
}

/** Same estimate as `estimateBytes`, generalised to a whole loaded `Object3D` (a server tile
 *  may be several meshes/materials, not just one) rather than a single parsed tile's arrays. */
function estimateObjectBytes(object: Object3D): number {
  let bytes = 0;
  const seenTextures = new Set<Texture>();
  object.traverse((child) => {
    if (!(child instanceof Mesh)) return;
    const geometry = child.geometry as BufferGeometry;
    for (const attribute of Object.values(geometry.attributes)) bytes += attribute.array.byteLength;
    if (geometry.index) bytes += geometry.index.array.byteLength;
    for (const material of materialsOf(child)) {
      const map = (material as MeshBasicMaterial).map;
      if (map && !seenTextures.has(map)) {
        seenTextures.add(map);
        bytes += estimateTextureBytes(map);
      }
    }
  });
  return bytes;
}

function estimateTextureBytes(texture: Texture): number {
  const image = texture.image as { width?: number; height?: number } | undefined;
  return Math.round((image?.width ?? 0) * (image?.height ?? 0) * 4 * 1.34);
}

function rgbToHex([r, g, b]: [number, number, number]): number {
  const to255 = (v: number) => Math.max(0, Math.min(255, Math.round(v * 255)));
  return (to255(r) << 16) | (to255(g) << 8) | to255(b);
}

function mimeTypeFor(path: string): string {
  const extension = path.slice(path.lastIndexOf(".") + 1).toLowerCase();
  if (extension === "png") return "image/png";
  if (extension === "webp") return "image/webp";
  return "image/jpeg";
}
